import { Component, OnDestroy, OnInit } from "@angular/core";
import { Subject, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { NgxSpinnerService } from "ngx-spinner";
import * as XLSX from "xlsx";
import swal from "sweetalert2";
import { LoginHistoryFilter, LoginHistoryModel } from "src/app/model/login-history.model";
import { NavigationLogService } from "src/app/service/navigation-log.service";

@Component({
  selector: "app-login-history",
  templateUrl: "./login-history.component.html",
  styleUrls: ["./login-history.component.scss"]
})
export class LoginHistoryComponent implements OnInit, OnDestroy {

  logs: LoginHistoryModel[] = [];
  currentPage: number = 1;
  pageSize: number = 20;
  totalItems: number = 0;
  term: string = "";
  action: string = "";
  startDate: string = "";
  endDate: string = "";
  actionOptions = [
    { value: "", label: "Todas as ações" },
    { value: "Login", label: "Login" },
    { value: "EnterPage", label: "Entrou na página" },
    { value: "LeavePage", label: "Saiu da página" }
  ];
  private searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;

  constructor(
    private navigationLogService: NavigationLogService,
    private spinner: NgxSpinnerService) {
  }

  ngOnInit() {
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(() => {
      this.currentPage = 1;
      this.loadLogs();
    });

    this.loadLogs();
  }

  ngOnDestroy() {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  onSearch(value: string) {
    this.searchSubject.next(value);
  }

  onFilterChange() {
    if (this.startDate && this.endDate && this.startDate > this.endDate) {
      swal.fire("Atenção", "A data inicial deve ser menor ou igual à data final.", "warning");
      return;
    }

    this.currentPage = 1;
    this.loadLogs();
  }

  clearFilters() {
    this.action = "";
    this.startDate = "";
    this.endDate = "";
    this.currentPage = 1;
    this.loadLogs();
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.loadLogs();
  }

  actionLabel(action: string): string {
    switch ((action || "").toLowerCase()) {
      case "login":
        return "Login";
      case "enterpage":
        return "Entrou na página";
      case "leavepage":
        return "Saiu da página";
      default:
        return action || "-";
    }
  }

  async doExport(): Promise<void> {
    this.spinner.show();

    try {
      const items = await this.fetchAllLogs();

      if (!items.length) {
        swal.fire("Atenção", "A busca não resultou em nenhum item.", "info");
        return;
      }

      this.generateExcel(items);
    } catch {
      swal.fire("Atenção", "Problema na geração do relatório, tente novamente.", "error");
    } finally {
      this.spinner.hide();
    }
  }

  private async fetchAllLogs(): Promise<LoginHistoryModel[]> {
    const items: LoginHistoryModel[] = [];
    const exportPageSize = 50;
    let pageNumber = 1;

    while (true) {
      const result = await this.navigationLogService
        .search(pageNumber, exportPageSize, this.buildFilter())
        .toPromise();

      const pageItems = result && result.items ? result.items : [];
      for (let item of pageItems) {
        items.push(item);
      }

      if (pageItems.length < exportPageSize) {
        break;
      }

      pageNumber += 1;
    }

    return items;
  }

  private generateExcel(logs: LoginHistoryModel[]): void {
    const workbook = XLSX.utils.book_new();
    const rows = logs.map(log => ({
      Nome: log.name || "-",
      Login: log.userName || "-",
      "Data / Hora": this.formatActionTime(log.actionTime),
      "IP de origem": log.remoteIpAddress || "-",
      Ação: this.actionLabel(log.action)
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Plan 1");

    workbook.Props = {};
    workbook.Props.Title = "EXPORT";
    workbook.Props.Subject = "EXPORT";

    XLSX.writeFile(workbook, `EXPORT${new Date().getTime()}.xlsx`);
  }

  private formatActionTime(value: string): string {
    if (!value) {
      return "-";
    }

    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return value;
    }

    const pad = (n: number) => (n < 10 ? "0" + n : "" + n);
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  }

  private buildFilter(): LoginHistoryFilter {
    return {
      searchQuery: this.term,
      action: this.action,
      startDate: this.toUtcIso(this.startDate, 0),
      // Fim exclusivo: meia-noite do dia seguinte, para incluir o dia final inteiro.
      endDate: this.toUtcIso(this.endDate, 1)
    };
  }

  // Converte "yyyy-MM-dd" (fuso do navegador) para ISO UTC.
  private toUtcIso(value: string, addDays: number): string {
    if (!value) {
      return undefined;
    }

    const parts = value.split("-").map(Number);
    if (parts.length !== 3 || parts.some(isNaN)) {
      return undefined;
    }

    return new Date(parts[0], parts[1] - 1, parts[2] + addDays).toISOString();
  }

  private loadLogs() {
    this.spinner.show();
    this.navigationLogService
      .search(this.currentPage, this.pageSize, this.buildFilter())
      .subscribe(
        result => {
          this.logs = result.items || [];
          this.totalItems = result.paging ? result.paging.totalItems : 0;
          this.currentPage = result.paging && result.paging.pageNumber
            ? result.paging.pageNumber
            : this.currentPage;
          this.spinner.hide();
        },
        () => {
          this.logs = [];
          this.totalItems = 0;
          this.spinner.hide();
        });
  }
}
